import React, { useState } from 'react';
import {
  TextField,
  FormControl,
  FormHelperText,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core';
import { IconContext } from 'react-icons';
import { MdExpandMore } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FiMail } from 'react-icons/fi';

import { DSLink, DSText } from '@engenharia/ds-react';

import Logo from '../../components/molecules/Logo';
import MetaHeader from '../../components/MetaHeader';
import { Container, Card, CardHeader, Form } from './styles';
import { mailValidation } from '../../utils/validations';
import authApi from '../../services/authApi';
import Notification from '../../components/Notification';
import Button from '../../components/atoms/Button';
import Header from '../../components/Header';
import { transcodeLanguage } from '../../utils/language';
import { getLanguage } from '../../services/auth';

export default function ConfirmEmail() {
  const { t } = useTranslation();
  const history = useHistory();
  const [disabledButton, setDisabledButton] = useState(false);
  const [state, setState] = useState({
    email: '',
    emailValidation: {},
  });

  const [expanded, setExpanded] = React.useState('');
  const [notification, setNotification] = useState({});

  const handleExpansionPanelChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleEmailChange = (event) => {
    setState({
      ...state,
      email: event.target.value,
      emailValidation: mailValidation(event.target.value),
    });
    setDisabledButton(false);
  };

  const clickActionButton = (link) => {
    history.push(link);
  };

  const notificationButton = (options) => (
    <DSLink onClick={() => clickActionButton(options.link)} variant="button" color="secondary">
      {options.message}
    </DSLink>
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { email } = state;

    setDisabledButton(true);

    try {
      if (!email) {
        setNotification({ key: new Date().getTime(), message: t('errors.email-invalid') });
      }

      const actionButton = notificationButton({
        message: t('confirmation-email.link-login'),
        link: '/',
      });

      const response = await authApi.get('/send_confirmation', {
        params: {
          email,
          language: transcodeLanguage(getLanguage()),
          product: process.env.REACT_APP_PRODUCT_KEY,
        },
      });

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message,
        button: actionButton,
      });
    } catch (error) {
      const { response } = error;

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  const expandMlabsIcon = () => (
    <IconContext.Provider value={{ color: '#1a73e8' }}>
      <div>
        <MdExpandMore />
      </div>
    </IconContext.Provider>
  );

  return (
    <>
      <Header positionFixed />
      <Container>
        <MetaHeader
          title="Não recebeu o e-mail de confirmação? - mLabs | Gestão de redes sociais"
          description="mLabs - Gestão de Redes sociais | E-mail de confirmação"
        />
        <Box>
          <Logo />
          <Box my={8} display="flex" flexDirection="row" flexWrap="wrap" justifyContent="center">
            <Box mx={2} component={Card}>
              <CardHeader>
                <DSText variant="h4" weight={400} color="primary">
                  {t('confirmation-email.title')}
                </DSText>
              </CardHeader>
              <DSText variant="body2" weight={400} color="textSecondary">
                {t('confirmation-email.description')}
              </DSText>
              <Form onSubmit={handleSubmit}>
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    required
                    type="email"
                    id="email"
                    name="email"
                    data-testid="formEmail"
                    label="Seu e-mail"
                    value={state.email}
                    onChange={handleEmailChange}
                    variant="outlined"
                    autoComplete="email"
                    margin="normal"
                    autoFocus
                    error={state.emailValidation?.emailInvalidError}
                  />

                  {!state.emailValidation.emailInvalidError && (
                    <FormHelperText id="email-error">
                      {t('confirmation-email.email-helper')}
                    </FormHelperText>
                  )}
                  {state.emailValidation.emailInvalidError && (
                    <FormHelperText error id="email-error">
                      {t('errors.email-invalid')}
                    </FormHelperText>
                  )}
                  <Box display="flex" alignSelf="flex-end">
                    <Button
                      mt={4}
                      type="submit"
                      id="btnSubmit"
                      data-testid="formSubmit"
                      color="primary"
                      startIcon={<FiMail />}
                      disabled={disabledButton}
                    >
                      {t('confirmation-email.button.send')}
                    </Button>
                  </Box>
                </FormControl>
              </Form>
              <Box display="flex" alignSelf="flex-end">
                <DSLink
                  variant="button"
                  underline="always"
                  href="/accounts/sign_in"
                  color="secondary"
                >
                  {t('confirmation-email.link-login')}
                </DSLink>
              </Box>
            </Box>
            <Box mx={2} component={Card}>
              <CardHeader>
                <DSText variant="h4" weight={400}>
                  {t('confirmation-email.title2')}
                </DSText>
              </CardHeader>
              <div>
                <Accordion
                  square
                  expanded={expanded === 'panel1'}
                  onChange={handleExpansionPanelChange('panel1')}
                >
                  <AccordionSummary
                    aria-controls="panel1d-content"
                    id="panel1d-header"
                    expandIcon={expandMlabsIcon()}
                  >
                    <DSText variant="h5" weight={400}>
                      {t('confirmation-email.faqs.first.title')}
                    </DSText>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DSText variant="body2" weight={400}>
                      {t('confirmation-email.faqs.first.text1')}
                      <br />
                      <br />
                      {t('confirmation-email.faqs.first.text2')}{' '}
                      <DSLink variant="body2" href="https://www.mlabs.com.br/contato">
                        {t('confirmation-email.faqs.first.text3')}
                      </DSLink>
                      .
                    </DSText>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  square
                  expanded={expanded === 'panel2'}
                  onChange={handleExpansionPanelChange('panel2')}
                >
                  <AccordionSummary
                    aria-controls="panel2d-content"
                    id="panel2d-header"
                    expandIcon={expandMlabsIcon()}
                  >
                    <DSText variant="h5" weight={400}>
                      {t('confirmation-email.faqs.second.title')}
                    </DSText>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DSText variant="body2" weight={400}>
                      {t('confirmation-email.faqs.second.text1')}
                      <br />
                      <br />
                      {t('confirmation-email.faqs.second.text2')}{' '}
                      <DSLink variant="body2" href="https://www.mlabs.com.br/contato">
                        {t('confirmation-email.faqs.second.text3')}
                      </DSLink>
                      .
                    </DSText>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  square
                  expanded={expanded === 'panel3'}
                  onChange={handleExpansionPanelChange('panel3')}
                >
                  <AccordionSummary
                    aria-controls="panel3d-content"
                    id="panel3d-header"
                    expandIcon={expandMlabsIcon()}
                  >
                    <DSText variant="h5" weight={400}>
                      {t('confirmation-email.faqs.third.title')}
                    </DSText>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DSText variant="body2" weight={400}>
                      {t('confirmation-email.faqs.third.text1')}{' '}
                      <DSLink variant="body2" href="https://www.mlabs.com.br/contato">
                        {t('confirmation-email.faqs.third.text2')}
                      </DSLink>
                      .
                    </DSText>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  square
                  expanded={expanded === 'panel4'}
                  onChange={handleExpansionPanelChange('panel4')}
                >
                  <AccordionSummary
                    aria-controls="panel4d-content"
                    id="panel4d-header"
                    expandIcon={expandMlabsIcon()}
                  >
                    <DSText variant="h5" weight={400}>
                      {t('confirmation-email.faqs.fourth.title')}
                    </DSText>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DSText variant="body2" weight={400}>
                      {t('confirmation-email.faqs.fourth.text1')}{' '}
                      <DSLink variant="body2" href="https://www.mlabs.com.br/contato">
                        {t('confirmation-email.faqs.fourth.text2')}
                      </DSLink>
                      . {t('confirmation-email.faqs.fourth.text3')}
                    </DSText>
                  </AccordionDetails>
                </Accordion>
              </div>
            </Box>
          </Box>
        </Box>
        {notification?.key && (
          <Notification
            index={notification.key}
            message={notification.message}
            button={notification.button}
          />
        )}
      </Container>
    </>
  );
}
